import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as UserService from '../../api/userService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';
import DatePicker from '../FormikAdapter/DatePicker';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  titleContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export interface Values {
  dob: moment.Moment | null;
}

const ValidationSchema = Yup.object().shape({
  dob: Yup.string().required('Required'),
});

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
  birthday: number | null;
  userId: string;
};

export default function UpdateDOBDialog({
  open,
  onClose,
  onSuccess,
  birthday,
  userId,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();

  const initialValues: Values = {
    dob: birthday ? moment.unix(birthday) : null,
  };

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    if (company && userId && values.dob) {
      try {
        await UserService.updateUserDOB({
          companyId: company.id,
          dob: values.dob.format('YYYY-MM-DD'),
          userID: userId,
        });
        onSuccess();
      } catch (e: any) {
        console.log('Error: ', e);
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form noValidate>
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.titleContainer}>
                          <Typography variant="h4">Edit Birthday</Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={DatePicker}
                          name="dob"
                          label="Birthday"
                          format="D MMM YYYY"
                          fullWidth
                          initvalue={birthday ? moment.unix(birthday) : null}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Button onClick={onClose}>Cancel</Button>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Save
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
